import React, { useState, useEffect, useMemo } from "react";
import SidbarComponent from "./SidbarComponent";
import TopBarComponent from "./TopBarComponent";
import { FooterComponents } from "./FooterComponent";
import axios from "axios";
import { getToken } from '../utils/auth';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Settings, Calendar, BookOpen, Play, Loader2, Lock, Unlock, ToggleLeft, ToggleRight, Shield, Database } from "lucide-react";

export default function SelectionComponent() {
  const [completionYear, setCompletionYear] = useState("");
  const [studyArea, setStudyArea] = useState("");
  const [academicYears, setAcademicYears] = useState([]);
  const [studyAreas, setStudyAreas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [updatingSettingId, setUpdatingSettingId] = useState(null);

  const queryClient = useQueryClient();

  const selectionTriger = async () => {
    if (completionYear && studyArea) {
      setIsLoading(true);
      await axios
        .get(
          process.env.REACT_APP_BASE_URL +
            `hec/settings/initiate?completionYearCode=${completionYear}&categoryCode=${studyArea}&key=${process.env.REACT_APP_KEY}`,
          {
            headers: {
              Authorization: "Bearer " + getToken(),
            },
          }
        )
        .then((response) => {
          toast.success("Selection initiation completed successfully!", {
            position: "top-center",
            autoClose: 5000,
            theme: "colored",
          });
        })
        .catch((error) => {
          if (error.response.status === 409) {
            toast.warning("No settings were found for the selected criteria", {
              position: "top-center",
              autoClose: 8000,
              theme: "colored",
            });
          } else {
            toast.error(
              "No local loans found for the given year and category",
              {
                position: "top-center",
                autoClose: 8000,
                theme: "colored",
              }
            );
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      toast.warning("Please select both completion year and study area", {
        position: "top-center",
        autoClose: 5000,
        theme: "colored",
      });
    }
  };

  const fetchAcademicYear = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_BASE_URL + "hec/academic/getAcademicYears"
      );
      const academicYearList = await response.data;
      setAcademicYears(academicYearList);
      return academicYearList;
    } catch {
      return null;
    }
  };

  useQuery({
    queryKey: ["academicYearKey"],
    queryFn: () => fetchAcademicYear(),
    retry: 6,
  });

  const fetchStudyAreas = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_BASE_URL + "hec/studyArea/getStudyAreas"
      );
      const studyAreasList = await response.data;
      setStudyAreas(studyAreasList);
      return studyAreasList;
    } catch {
      return null;
    }
  };

  useQuery({
    queryKey: ["areasKey"],
    queryFn: () => fetchStudyAreas(),
    retry: 6,
  });

  const fetchLoanSettings = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_BASE_URL + "loanOpened/getLoanRequestOpned",
        {
          headers: {
            Authorization: "Bearer " + getToken(),
          },
        }
      );
      const settings = await response.data;

      return settings;
    } catch (error) {
      console.error("Error fetching loan settings:", error);
      toast.error("Failed to fetch loan settings", {
        position: "top-center",
        autoClose: 5000,
        theme: "colored",
      });
      throw error; // Re-throw to let React Query handle the error state
    }
  };

  // Loan settings query with proper error handling and longer cache time
  const {
    data: loanSettings = [],
    isLoading: loanSettingsLoading,
    isError: loanSettingsError,
    error: loanSettingsErrorDetails,
    refetch: refetchLoanSettings
  } = useQuery({
    queryKey: ["loanSettings"],
    queryFn: fetchLoanSettings,
    retry: 3,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false, // Prevent unnecessary refetches
    refetchOnMount: true, // Always fetch when component mounts
    enabled: !!getToken(), // Only run query if token exists
  });

  // Handle loan settings error state
  useEffect(() => {
    if (loanSettingsError && loanSettingsErrorDetails) {
      console.error("Loan settings query error:", loanSettingsErrorDetails);
    }
  }, [loanSettingsError, loanSettingsErrorDetails]);

  // Fetch all HLI institutions so we can show per-institution loan status
  const { data: hliList = [] } = useQuery({
    queryKey: ["allHliList"],
    queryFn: async () => {
      const response = await axios.get(
        process.env.REACT_APP_BASE_URL + "hec/hli/getAllHli",
        { headers: { Authorization: "Bearer " + getToken() } }
      );
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: !!getToken(),
  });

  // Merge HLI list with loan settings records
  const institutionLoanStatus = useMemo(() => {
    return hliList.map((hli) => {
      const setting = loanSettings.find(
        (s) => s.institution && s.institution.id === hli.id
      );
      return {
        institutionId: hli.id,
        institutionName: hli.fullName || hli.institutionCode,
        loanRequestOpened: setting ? setting.loanRequestOpened : false,
        settingId: setting ? setting.id : null,
      };
    });
  }, [hliList, loanSettings]);

  // Update loan setting status per institution
  const updateLoanSettingMutation = useMutation({
    mutationFn: async ({ institutionId, newStatus }) => {
      const response = await axios.put(
        process.env.REACT_APP_BASE_URL +
          `loanOpened/updateLoanStatusByInstitution?institutionId=${institutionId}&status=${newStatus}`,
        {},
        {
          headers: {
            Authorization: "Bearer " + getToken(),
            "Content-Type": "application/json",
          },
        }
      );
      return { ...response.data, institutionId, newStatus };
    },
    onMutate: async (variables) => {
      setUpdatingSettingId(variables.institutionId);
      await queryClient.cancelQueries(["loanSettings"]);
      const previousSettings = queryClient.getQueryData(["loanSettings"]);
      queryClient.setQueryData(["loanSettings"], (old) => {
        if (!old) return old;
        const exists = old.some(
          (s) => s.institution && s.institution.id === variables.institutionId
        );
        if (exists) {
          return old.map((setting) =>
            setting.institution && setting.institution.id === variables.institutionId
              ? { ...setting, loanRequestOpened: variables.newStatus }
              : setting
          );
        }
        // optimistically add a placeholder entry
        return [
          ...old,
          {
            id: null,
            institution: { id: variables.institutionId },
            loanRequestOpened: variables.newStatus,
          },
        ];
      });
      return { previousSettings };
    },
    onSuccess: (data, variables) => {
      toast.success(
        `Loan applications ${variables.newStatus ? "opened" : "closed"} successfully!`,
        { position: "top-center", autoClose: 3000, theme: "colored" }
      );
    },
    onError: (error, variables, context) => {
      if (context?.previousSettings) {
        queryClient.setQueryData(["loanSettings"], context.previousSettings);
      }
      toast.error("Failed to update loan setting", {
        position: "top-center",
        autoClose: 5000,
        theme: "colored",
      });
    },
    onSettled: (data, error, variables) => {
      setUpdatingSettingId(null);
      queryClient.invalidateQueries(["loanSettings"]);
    },
  });

  const handleStatusUpdate = (institutionId, currentStatus) => {
    updateLoanSettingMutation.mutate({
      institutionId,
      newStatus: !currentStatus,
    });
  };

  return (
    <div className="bg-slate-100 min-h-screen flex flex-col">
      <TopBarComponent />
      <aside
        id="logo-sidebar"
        className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0"
        aria-label="Sidebar"
      >
        <SidbarComponent />
      </aside>

      <div className="p-6 sm:ml-64 transition-all duration-300 flex-1 flex flex-col">
        {/* Page Header */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <Settings className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Selection & Loan Management</h1>
                  <p className="text-gray-600 mt-1">Configure selection criteria and manage loan request settings</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            {/* Selection Criteria Section */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
              {/* Form Header */}
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-r from-cyan-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Settings className="w-8 h-8 text-cyan-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Selection Criteria</h2>
                <p className="text-gray-600">Please select the completion year and study area to initiate the selection process</p>
              </div>

              {/* Form */}
              <div className="space-y-6">
                {/* Completion Year */}
                <div>
                  <label className="flex items-center text-sm font-semibold text-gray-700 mb-3">
                    <Calendar className="w-4 h-4 mr-2 text-cyan-600" />
                    Completion Year
                  </label>
                  <select
                    onChange={(e) => setCompletionYear(e.target.value)}
                    value={completionYear}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white"
                  >
                    <option value="">Select completion year</option>
                    {academicYears &&
                      academicYears.map((academicYear) => (
                        <option
                          key={academicYear.id}
                          value={academicYear.academicYearName}
                        >
                          {academicYear.academicYearName}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Study Area */}
                <div>
                  <label className="flex items-center text-sm font-semibold text-gray-700 mb-3">
                    <BookOpen className="w-4 h-4 mr-2 text-cyan-600" />
                    Study Area
                  </label>
                  <select
                    onChange={(e) => setStudyArea(e.target.value)}
                    value={studyArea}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white"
                  >
                    <option value="">Select study area</option>
                    {studyAreas &&
                      studyAreas.map((study) => (
                        <option key={study.id} value={study.studyAreaName}>
                          {study.studyAreaName}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <button
                    onClick={() => selectionTriger()}
                    disabled={isLoading || !completionYear || !studyArea}
                    className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-4 px-6 rounded-xl font-semibold text-lg 
                             hover:from-cyan-700 hover:to-blue-700 transition-all duration-200 transform hover:scale-[1.02] 
                             disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
                             shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <Play className="w-5 h-5" />
                        <span>Initiate Selection</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Info Card */}
              <div className="mt-8 bg-cyan-50 border border-cyan-200 rounded-xl p-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Settings className="w-3 h-3 text-cyan-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-cyan-800 mb-1">Selection Process</p>
                    <p className="text-sm text-cyan-700">
                      This will initiate the selection process for students based on the specified completion year and study area. 
                      Make sure both criteria are selected before proceeding.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Loan Settings Section */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
              {/* Settings Header */}
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-r from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Loan Request Settings</h2>
                <p className="text-gray-600">Manage loan request opening and closing status</p>
              </div>

              {/* Settings Table */}
              <div className="space-y-4">
                {loanSettingsLoading ? (
                  <div className="text-center py-8 sm:py-12">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                      <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 text-green-600 animate-spin" />
                    </div>
                    <p className="text-gray-600 text-base sm:text-lg font-medium mb-1 sm:mb-2">Loading Settings</p>
                    <p className="text-gray-500 text-sm px-4">Please wait while we fetch the loan request settings...</p>
                  </div>
                ) : loanSettingsError ? (
                  <div className="text-center py-8 sm:py-12">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                      <Database className="w-6 h-6 sm:w-8 sm:h-8 text-red-500" />
                    </div>
                    <p className="text-red-600 text-base sm:text-lg font-medium mb-1 sm:mb-2">Failed to Load Settings</p>
                    <p className="text-gray-500 text-sm px-4 mb-4">There was an error loading the loan request settings.</p>
                    <button
                      onClick={() => refetchLoanSettings()}
                      className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
                    >
                      <Loader2 className="w-4 h-4" />
                      <span>Retry</span>
                    </button>
                  </div>
                ) : institutionLoanStatus.length > 0 ? (
                  <>
                    {institutionLoanStatus.map((item) => (
                      <div key={item.institutionId} className="border border-gray-200 rounded-xl p-4 sm:p-6 hover:shadow-md transition-shadow duration-200">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                          <div className="flex items-center space-x-3 sm:space-x-4 flex-1">
                            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                              item.loanRequestOpened
                                ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                                : 'bg-gradient-to-r from-red-500 to-pink-500'
                            }`}>
                              {item.loanRequestOpened ? (
                                <Unlock className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                              ) : (
                                <Lock className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                                {item.institutionName}
                              </h3>
                              <div className="flex items-center justify-between sm:justify-start sm:space-x-4 mt-1">
                                <span className={`inline-flex items-center space-x-1 sm:space-x-2 px-2 py-1 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold ${
                                  item.loanRequestOpened
                                    ? 'bg-green-100 text-green-800 border border-green-200'
                                    : 'bg-red-100 text-red-800 border border-red-200'
                                }`}>
                                  {item.loanRequestOpened ? (
                                    <><Unlock className="w-3 h-3 sm:w-4 sm:h-4" /><span>Open</span></>
                                  ) : (
                                    <><Lock className="w-3 h-3 sm:w-4 sm:h-4" /><span>Closed</span></>
                                  )}
                                </span>

                                <button
                                  onClick={() => handleStatusUpdate(item.institutionId, item.loanRequestOpened)}
                                  disabled={updatingSettingId === item.institutionId}
                                  className={`relative inline-flex h-8 w-14 sm:h-12 sm:w-20 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                                    item.loanRequestOpened
                                      ? 'bg-green-500 focus:ring-green-500'
                                      : 'bg-red-500 focus:ring-red-500'
                                  } disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0`}
                                >
                                  <span className="sr-only">Toggle loan request status</span>
                                  <div className={`inline-block h-6 w-6 sm:h-8 sm:w-8 transform rounded-full bg-white shadow-lg ring-0 transition-transform duration-200 ${
                                    item.loanRequestOpened ? 'translate-x-6 sm:translate-x-10' : 'translate-x-1'
                                  }`}>
                                    {updatingSettingId === item.institutionId ? (
                                      <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 animate-spin text-gray-600 m-1.5 sm:m-2" />
                                    ) : item.loanRequestOpened ? (
                                      <Unlock className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 m-1.5 sm:m-2" />
                                    ) : (
                                      <Lock className="w-3 h-3 sm:w-4 sm:h-4 text-red-600 m-1.5 sm:m-2" />
                                    )}
                                  </div>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Description */}
                        <div className="mt-4 pl-0 sm:pl-16">
                          <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                            {item.loanRequestOpened
                              ? `Students from ${item.institutionName} can currently submit loan requests. Click to close.`
                              : `Loan requests are closed for ${item.institutionName}. Click to open submissions.`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="text-center py-8 sm:py-12">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                      <Database className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-600 text-base sm:text-lg font-medium mb-1 sm:mb-2">No Institutions Found</p>
                    <p className="text-gray-500 text-sm px-4">No higher learning institutions are currently configured.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <FooterComponents />
      </div>
    </div>
  );
}
